import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, UrlTree } from '@angular/router';
import { catchError, finalize, map, Observable, of } from 'rxjs';
import { ExternalAssistantAuthService } from 'src/app/services/ai/external-assistant-auth.service';
import { CommonService } from 'src/app/services/common/common.service';

/** Child segment of the access denied page the guard redirects to when the external token cannot be generated */
export const EXTERNAL_ASSISTANT_ACCESS_DENIED_SEGMENT = 'access-denied';

@Injectable({
  providedIn: 'root'
})
export class ExternalAssistantGuard implements CanActivate {
  /**
   * constructor
   * @param externalAssistantAuthService
   * @param router
   * @param commonService
   */
  constructor(
    private readonly externalAssistantAuthService: ExternalAssistantAuthService,
    private readonly router: Router,
    private readonly commonService: CommonService
  ) {}

  /**
   * Fetches the external token for the tenant; blocks the page when the token API fails or returns no token.
   * @param route
   * @returns
   */
  canActivate(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> | boolean {
    const tenantCode = route.paramMap.get('tenantCode') || '';
    if (!tenantCode) {
      return true;
    }

    this.commonService.showLoading();
    return this.externalAssistantAuthService.fetchAndStoreTokenByTenantCode(tenantCode).pipe(
      map((tokenResponse) => (tokenResponse.accessToken ? true : this.accessDeniedUrl(route, tenantCode))),
      catchError(() => of(this.accessDeniedUrl(route, tenantCode))),
      finalize(() => this.commonService.hideLoading())
    );
  }

  /**
   * Access denied page for the tenant, keeping the embed query params for theming and retry.
   * @param route
   * @param tenantCode
   * @returns
   */
  private accessDeniedUrl(route: ActivatedRouteSnapshot, tenantCode: string): UrlTree {
    return this.router.createUrlTree(
      [ '/ai-agent-external-assistant', tenantCode, EXTERNAL_ASSISTANT_ACCESS_DENIED_SEGMENT ],
      { queryParams: route.queryParams }
    );
  }
}
